import { Component, createRef } from 'preact';
import ClutterfoxLogo from './ClutterfoxLogo';
import {
  DIALOG_STYLE,
  FourCorners,
  OrnamentalDivider,
  TEXT_PRIMARY,
  TEXT_BODY,
  TEXT_MUTED,
  ACCENT_GOLD,
} from '../lib/medievalTheme';
import { fanProjectDisclaimerParagraphs, FAN_PROJECT_FOOTNOTE } from './fanProjectDisclaimer';
import { pushDismiss, removeDismiss } from '../utils/escStack';
import { t } from '../localization/t';
import './IntroSplash.css';

const PHASE_SCHEDULE = [
  { name: 'logo-in', durationMs: 800 },
  { name: 'logo-hold', durationMs: 1400 },
  { name: 'logo-out', durationMs: 500 },
  { name: 'gap', durationMs: 350 },
  { name: 'disclaimer-in', durationMs: 800 },
  { name: 'disclaimer-hold', durationMs: 7000 },
  { name: 'disclaimer-out', durationMs: 550 },
];

// ~30 embers is plenty for ambience without taxing integrated GPUs.
const EMBER_COUNT = 30;

const LOGO_PHASES = new Set(['logo-in', 'logo-hold', 'logo-out', 'gap']);

export default class IntroSplash extends Component {
  constructor(props) {
    super(props);
    this.state = {
      phaseIndex: 0,
      phaseName: PHASE_SCHEDULE[0].name,
      skipping: false,
    };
    this.canvasRef = createRef();
    this.rootRef = createRef();
    this.phaseTimer = null;
    this.rafHandle = null;
    this.embers = [];
    this.lastFrameMs = 0;
  }

  componentDidMount() {
    this._escDismissId = pushDismiss(() => this.skip());
    this.scheduleNextPhase();
    this.initEmbers();
    this.rafHandle = requestAnimationFrame(this.tickEmbers);
    // Focus the root so screen readers announce the intro and so the
    // keydown listener gets priority over any background components.
    this.rootRef.current?.focus();
  }

  componentDidUpdate(_prevProps, prevState) {
    // Stop the embers rAF once the logo phase ends. The disclaimer
    // uses the medieval aesthetic, no floating embers.
    const wasLogo = LOGO_PHASES.has(prevState.phaseName);
    const isLogo = LOGO_PHASES.has(this.state.phaseName);
    if (wasLogo && !isLogo && this.rafHandle) {
      cancelAnimationFrame(this.rafHandle);
      this.rafHandle = null;
    }
  }

  componentWillUnmount() {
    removeDismiss(this._escDismissId);
    if (this.phaseTimer) clearTimeout(this.phaseTimer);
    if (this.rafHandle) cancelAnimationFrame(this.rafHandle);
  }

  skip() {
    if (this.state.skipping) return;
    if (this.phaseTimer) clearTimeout(this.phaseTimer);
    this.setState({ skipping: true });
    // One short fade-to-black so the skip doesn't feel like a cut.
    setTimeout(() => this.props.onComplete?.(), 280);
  }

  scheduleNextPhase = () => {
    const { phaseIndex } = this.state;
    const current = PHASE_SCHEDULE[phaseIndex];
    this.phaseTimer = setTimeout(() => {
      const nextIndex = phaseIndex + 1;
      if (nextIndex >= PHASE_SCHEDULE.length) {
        this.props.onComplete?.();
        return;
      }
      this.setState(
        { phaseIndex: nextIndex, phaseName: PHASE_SCHEDULE[nextIndex].name },
        this.scheduleNextPhase
      );
    }, current.durationMs);
  };

  initEmbers() {
    const canvas = this.canvasRef.current;
    if (!canvas) return;
    const { width, height } = this.resizeCanvas(canvas);
    this.embers = Array.from({ length: EMBER_COUNT }, () => this.spawnEmber(width, height, true));
  }

  spawnEmber(width, height, seeded = false) {
    return {
      x: Math.random() * width,
      // When seeded on mount, distribute across full height so the
      // screen doesn't start empty. Otherwise new embers spawn below
      // the viewport and drift up.
      y: seeded ? Math.random() * height : height + Math.random() * 40,
      vx: (Math.random() - 0.5) * 0.25,
      vy: -(0.35 + Math.random() * 0.5),
      size: 1 + Math.floor(Math.random() * 3),
      life: Math.random() * 0.5 + 0.5,
      lifeDecay: 0.0015 + Math.random() * 0.002,
      hue: 18 + Math.random() * 20,
    };
  }

  resizeCanvas(canvas) {
    // Cap at 2: soft ember blobs resolve nothing past that, and a full-screen
    // dpr-3 backing store is pure memory cost on phones.
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const rect = canvas.getBoundingClientRect();
    canvas.width = Math.max(1, Math.floor(rect.width * dpr));
    canvas.height = Math.max(1, Math.floor(rect.height * dpr));
    const ctx = canvas.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    return { width: rect.width, height: rect.height, ctx };
  }

  tickEmbers = (ts) => {
    this.rafHandle = requestAnimationFrame(this.tickEmbers);
    const canvas = this.canvasRef.current;
    if (!canvas) return;
    if (!this.lastFrameMs) this.lastFrameMs = ts;
    const dt = Math.min(48, ts - this.lastFrameMs);
    this.lastFrameMs = ts;

    const { width, height, ctx } = this.resizeCanvas(canvas);
    ctx.clearRect(0, 0, width, height);
    ctx.globalCompositeOperation = 'lighter';

    const step = dt / 16;
    for (let i = 0; i < this.embers.length; i++) {
      const e = this.embers[i];
      e.x += e.vx * step;
      e.y += e.vy * step;
      e.life -= e.lifeDecay * step;
      if (e.life <= 0 || e.y < -10) {
        this.embers[i] = this.spawnEmber(width, height, false);
        continue;
      }
      const alpha = Math.max(0, Math.min(1, e.life));
      ctx.fillStyle = `hsla(${e.hue}, 85%, 58%, ${alpha})`;
      ctx.beginPath();
      ctx.arc(e.x, e.y, e.size, 0, Math.PI * 2);
      ctx.fill();
    }
  };

  renderDisclaimer() {
    return (
      <div
        className="intro-splash__disclaimer-card"
        style={{ ...DIALOG_STYLE, position: 'relative' }}
      >
        <FourCorners />
        <div className="intro-splash__disclaimer-inner">
          <div
            className="intro-splash__disclaimer-eyebrow"
            style={{ color: ACCENT_GOLD }}
          >
            Valkenhall
          </div>
          <div
            className="arena-heading intro-splash__disclaimer-heading"
            style={{
              color: TEXT_PRIMARY,
              textShadow: '0 2px 10px rgba(0,0,0,0.92), 0 0 28px rgba(180,140,60,0.22)',
            }}
          >
            {t('A Fan-Made Project')}
          </div>

          <OrnamentalDivider className="intro-splash__disclaimer-divider" />

          {fanProjectDisclaimerParagraphs().map((content, i) => (
            <p
              key={i}
              className="intro-splash__disclaimer-body"
              style={{ color: TEXT_BODY }}
            >
              {content}
            </p>
          ))}

          <p className="intro-splash__disclaimer-footnote" style={{ color: TEXT_MUTED }}>
            {FAN_PROJECT_FOOTNOTE}
          </p>
        </div>
      </div>
    );
  }

  render() {
    const { phaseName, skipping } = this.state;
    const showLogo = phaseName === 'logo-in' || phaseName === 'logo-hold' || phaseName === 'logo-out';
    const showDisclaimer =
      phaseName === 'disclaimer-in' || phaseName === 'disclaimer-hold' || phaseName === 'disclaimer-out';

    // Pixel-art / CRT ambience is a Clutterfox Studios thing only.
    // the disclaimer drops into Valkenhall's medieval aesthetic.
    const isLogoPhase = showLogo || phaseName === 'gap';
    const [skipBefore, skipAfter] = t('Press {key} to skip').split('{key}');

    return (
      <div
        ref={this.rootRef}
        className={`intro-splash intro-splash--${phaseName}${skipping ? ' is-skipping' : ''}${
          isLogoPhase ? ' intro-splash--pixel' : ' intro-splash--medieval'
        }`}
        tabIndex={-1}
      >
        {isLogoPhase ? (
          <>
            <canvas ref={this.canvasRef} className="intro-splash__embers" aria-hidden="true" />
            <div className="intro-splash__vignette" aria-hidden="true" />
            <div className="intro-splash__scanlines" aria-hidden="true" />
            <div className="intro-splash__flicker" aria-hidden="true" />
          </>
        ) : (
          <div className="intro-splash__medieval-vignette" aria-hidden="true" />
        )}

        <div className="intro-splash__stage">
          {showLogo ? (
            <div
              className={`intro-splash__panel intro-splash__panel--logo is-${
                phaseName === 'logo-in' ? 'in' : phaseName === 'logo-out' ? 'out' : 'hold'
              }`}
            >
              <ClutterfoxLogo scale={12} />
            </div>
          ) : null}

          {showDisclaimer ? (
            <div
              className={`intro-splash__panel intro-splash__panel--disclaimer is-${
                phaseName === 'disclaimer-in' ? 'in' : phaseName === 'disclaimer-out' ? 'out' : 'hold'
              }`}
            >
              {this.renderDisclaimer()}
            </div>
          ) : null}
        </div>

        <div className="intro-splash__skip-hint" aria-hidden="true">
          {skipBefore}<kbd>ESC</kbd>{skipAfter}
        </div>
      </div>
    );
  }
}
